import React, { DragEvent, MouseEvent, TouchEvent as ReactTouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { actuators as actuatorTemplates } from '../../actuators';
import { applianceTypes } from '../../applianceTypes';
import Button from '../../atoms/Button/Button';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import { detectors as detectorTemplates } from '../../detectors';
import { getActuatorNumberOfChannels, getConnectedApplianceIds, getUsedActuatorChannels, isApplianceConnectedToActuator, removeAppliancesFromActuator } from '../../Functions/ActuatorChannels';
import { addPublicURL } from '../../Functions/AddPublicURL';
import { getDetectorConnectedActuatorIds } from '../../Functions/DetectorConnections';
import { FLOORPLAN_EXPORT_TARGET_ID } from '../../Functions/ExportToPdf';
import { formatOptionalEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { getFromLocalStorage } from '../../Functions/LocalStorageUtils';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { setSelectedAppliance } from '../../redux/reducers/applianceReducer';
import {
    disconnectControllerFromAllAppliances,
    setActuatorControllerLinks,
    setActuatorPosition,
    setActuators,
    setAppliances,
    setBottomBarOpen,
    setDetectors,
    setHoveredItem,
    setSelectingDevice,
    setShowActuatorDetail,
    setShowAddDevicePopup,
    setShowApplianceDetail,
    setShowDetectorDetail,
    setShowDeviceDetail,
    setShowThermostatDetail,
    syncApplianceControllerConnections
} from '../../redux/reducers/appReducer';
import {
    removeApplianceFromAllControllers,
    removeDevice,
    setDevicePosition,
    setSelectedDeviceId,
    setSelectedDevicePosition,
    syncControllerChannelConnections
} from '../../redux/reducers/controllersReducer';
import { setSelectedDetector } from '../../redux/reducers/detectorsReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ConnectedActuatorChannelI, DeviceI, IconsI } from '../../redux/types/controllers';

import styles from './FloorplanComponent.module.scss';

interface DroppedFloorplanItem {
    tech?: string;
    id: number;
    icon: string;
    title: string;
    type: string;
    modelType?: string;
    applianceType: string;
    state: string;
    connectedTo?: number[];
    x: number;
    y: number;
    dinrail?: boolean;
}

interface ApplianceToHydrate {
    id: number;
    icon: string;
    label: string;
    applianceType: string;
    state?: string;
    connectedTo?: number[];
    x?: number;
    y?: number;
}

interface DraggableMenuItem {
    icon: string;
    title: string;
    type: 'appliance' | 'controller' | 'detector',
    state: string;
    dinrail?: boolean;
}

type ActiveDraggedItem = {
    kind: 'appliance' | 'controller' | 'actuator' | 'detector';
    id: number;
    title: string;
    imgSrc: string;
};

type NormalizedPosition = { x: number; y: number };
type ApplianceTypeKey = keyof typeof applianceTypes;
type EasyModeItems = (typeof applianceTypes)[ApplianceTypeKey];
type HighlightKind = 'controller' | 'appliance' | 'actuator' | 'detector';
type LineKind = HighlightKind;
type TieLinesMode = 'onHover' | 'always' | 'never';
type EasyModePopupStep = 'selectActuator' | 'addControllerQuestion';
type DinrailConnectionMarker = {
    key: string;
    actuatorId: number;
    applianceId: number;
    controllerId: number;
    x: number;
    y: number;
    icon: string;
    title: string;
};

const FloorplanComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const controllers = useAppSelector((state) => state.controllers.devices);
    const hoveredItem = useAppSelector((state) => state.app.hoveredItem);
    const appliances = useAppSelector((state) => state.app.appliances);
    const actuators = useAppSelector((state) => state.app.actuators);
    const detectors = useAppSelector((state) => state.app.detectors);
    const floorplanImage = useAppSelector((state) => state.app.floorplanImage);
    const configurationResetRevision = useAppSelector((state) => state.app.configurationResetRevision);
    const leftMenuShown = useAppSelector((state) => state.app.leftMenuShown);
    const rightMenuShown = useAppSelector((state) => state.app.rightMenuShown);
    const appliancesRef = useRef(appliances);
    appliancesRef.current = appliances;
    const actuatorsRef = useRef(actuators);
    actuatorsRef.current = actuators;
    const detectorsRef = useRef(detectors);
    detectorsRef.current = detectors;
    const [droppedItems, setDroppedItems] = useState<DroppedFloorplanItem[]>([]);
    const [droppedActuators, setDroppedActuators] = useState<DroppedFloorplanItem[]>([]);
    const [droppedDetectors, setDroppedDetectors] = useState<DroppedFloorplanItem[]>([]);
    const [controllerPositions, setControllerPositions] = useState<Record<number, NormalizedPosition>>({});
    const [activeDraggedItem, setActiveDraggedItem] = useState<ActiveDraggedItem | null>(null);
    const [dragCursorPosition, setDragCursorPosition] = useState<{ x: number; y: number } | null>(null);
    const [activePointerType, setActivePointerType] = useState<'mouse' | 'touch' | null>(null);
    const [iconSize, setIconSize] = useState('6rem');
    const [dropZoneSize, setDropZoneSize] = useState({ width: 1, height: 1 });
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    const activeTouchIdentifierRef = useRef<number | null>(null);
    const nextDroppedApplianceIdRef = useRef(1);
    const nextDroppedActuatorIdRef = useRef(1);
    const nextDroppedDetectorIdRef = useRef(1);
    const prevDroppedItemIdsRef = useRef<Set<number>>(new Set());
    const hasHydratedAppliancesRef = useRef(false);
    const hasHydratedActuatorsRef = useRef(false);
    const hasHydratedDetectorsRef = useRef(false);
    const lastActuatorSyncSignatureRef = useRef('');
    const lastControllerSyncSignatureRef = useRef('');
    const [easyModeDropPopup, setEasyModeDropPopup] = useState<{ applianceId: number; x: number; y: number; items?: EasyModeItems } | null>(null);
    const [easyModeSelectedActuator, setEasyModeSelectedActuator] = useState('');
    const [easyModePopupStep, setEasyModePopupStep] = useState<EasyModePopupStep>('selectActuator');
    const isEasyMode = getFromLocalStorage('actuatorsEasyMode') === 'true';
    const showActuators = true;
    const tieLinesMode = (getFromLocalStorage('tieLinesMode') as TieLinesMode) || 'onHover';
    const showBackgroundColors = getFromLocalStorage('showBackgroundColors') === '' ? true : getFromLocalStorage('showBackgroundColors') === 'true';

    const deleteController = useCallback((controllerId: number) => {
        dispatch(disconnectControllerFromAllAppliances(controllerId));
        dispatch(removeDevice(controllerId));
        // Remove linked built-in actuator and disconnect its appliances
        const linkedActuator = actuatorsRef.current.find((a) => a.builtIn && a.linkedControllerId === controllerId);
        if (linkedActuator?.id !== undefined) {
            const linkedActuatorId = linkedActuator.id;
            dispatch(setActuators(actuatorsRef.current.filter((a) => a.id !== linkedActuatorId)));
            dispatch(
                setAppliances(
                    appliancesRef.current.map((appliance) => {
                        if (appliance.actuator === linkedActuatorId) {
                            return { ...appliance, actuator: undefined };
                        }
                        return appliance;
                    })
                )
            );
        }
    }, [dispatch]);

    const showDeviceDetail = (deviceId: number, deviceType: string) => {
        dispatch(setSelectedDeviceId(deviceId));
        switch(deviceType){
            case 'thermostat':
                dispatch(setShowThermostatDetail(true));
            break;
            case 'controller':
                dispatch(setShowDeviceDetail(true));
            break;
            default: 
                toast.error(t('rightMenu.devices.error'));
            break;
        }
    };

    const showApplianceDetail = (applianceId: number) => {
        const appliance = appliancesRef.current.find(a => a.id === applianceId);
        if(!appliance){
            toast.error(t('rightMenu.appliances.error'));
            return;
        }
        dispatch(setSelectedAppliance({
            id: appliance.id,
            label: appliance.label,
            icon: appliance.icon,
            type: appliance.type,
            applianceType: appliance.applianceType,
            state: appliance.state ?? '',
            actuator: appliance.actuator ?? 0,
            connectedTo: appliance.connectedTo ?? []
        }));
        dispatch(setShowApplianceDetail(true));
    }

    const showActuatorDetail = (actuatorId: number) => {
        const actuator = actuatorsRef.current.find(a => a.id === actuatorId);
        if(!actuator){
            toast.error(t('rightMenu.actuators.error'));
            return;
        }
        dispatch(setSelectedActuator(actuator));
        dispatch(setShowActuatorDetail(true));
    };

    const showDetectorDetail = (detectorId: number) => {
        const detector = detectorsRef.current.find(d => d.id === detectorId);
        if(!detector){
            toast.error(t('rightMenu.detectors.error'));
            return;
        }
        dispatch(setSelectedDetector(detector));
        dispatch(setShowDetectorDetail(true));
    };

    const getDetectorTemplate = useCallback((item: { title?: string; icon?: string }) => {
        return detectorTemplates.find((template) => template.name === item.title)
            ?? detectorTemplates.find((template) => template.refImg === item.icon);
    }, []);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const dropZoneWidth = entry.contentRect.width;
                const dropZoneHeight = entry.contentRect.height;
                const iconPx = Math.max(40, Math.min(60, dropZoneWidth * 0.9 * 0.05));
                setIconSize(`${iconPx / 10}rem`);
                setDropZoneSize({ width: dropZoneWidth || 1, height: dropZoneHeight || 1 });
            }
        });

        const currentRef = dropZoneRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => observer.disconnect();
    }, []);

    const checkIfShouldShowAddDevicePopup = () => {
        setTimeout(() => {
            let controllersPositions = 0;
            controllers.forEach((controller) => {
                const positions = controller.iconsData;
                if(controller.type === 'thermostat' && (positions[0].applianceId ?? []).length === 0) controllersPositions++;
                positions.forEach((position) => {
                    if((position.applianceId ?? []).length === 0) controllersPositions++;
                })
            });

            if(appliances.length > 4){
                if(((appliances.length * 100)/controllersPositions)-100 >= 100){
                    dispatch(setShowAddDevicePopup(true));
                }
            }
        }, 800);
    };

    const getNormalizedDropPosition = (clientX: number, clientY: number) => {
        if (!dropZoneRef.current) {
            return null;
        }

        const dropZoneRect = dropZoneRef.current.getBoundingClientRect();
        const relativeX = ((clientX - dropZoneRect.left) / dropZoneRect.width) * 100;
        const relativeY = ((clientY - dropZoneRect.top) / dropZoneRect.height) * 100;

        return {
            x: Math.max(0, Math.min(100, relativeX)),
            y: Math.max(0, Math.min(100, relativeY)),
            isInside:
                clientX >= dropZoneRect.left &&
                clientX <= dropZoneRect.right &&
                clientY >= dropZoneRect.top &&
                clientY <= dropZoneRect.bottom
        };
    };

    const getActuatorTemplate = useCallback((item: { modelType?: string; type?: string; title?: string; icon?: string; refImg?: string }) => {
        if (item.modelType) {
            return actuatorTemplates.find((template) => template.type === item.modelType);
        }

        if (item.type) {
            return actuatorTemplates.find((template) => template.type === item.type)
                ?? actuatorTemplates.find((template) => template.refImg === item.refImg)
                ?? actuatorTemplates.find((template) => template.name === item.title && template.icon === item.icon)
                ?? actuatorTemplates.find((template) => template.icon === item.icon);
        }

        return actuatorTemplates.find((template) => template.refImg === item.refImg)
            ?? actuatorTemplates.find((template) => template.name === item.title && template.icon === item.icon)
            ?? actuatorTemplates.find((template) => template.icon === item.icon);
    }, []);

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const isPlacedItemDrag = event.dataTransfer.types.includes('application/floorplan-placed-item');
        event.dataTransfer.dropEffect = isPlacedItemDrag ? 'move' : 'copy';
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        const normalizedDropPosition = getNormalizedDropPosition(event.clientX, event.clientY);

        if (!normalizedDropPosition) {
            return;
        }

        const serializedItem = event.dataTransfer.getData('application/floorplan-item');
        const type = event.dataTransfer.getData('application/floorplan-item-type');
        const state = event.dataTransfer.getData('application/floorplan-item-state');
        const applianceType = event.dataTransfer.getData('application/floorplan-itemtype');

        if (!serializedItem) {
            return;
        }

        let droppedItem: DraggableMenuItem;

        try {
            droppedItem = JSON.parse(serializedItem) as DraggableMenuItem;
        } catch {
            return;
        }


        const droppedCategoryType =
            type === 'controller' || type === 'appliance' || type === 'actuator' || type === 'detector'
                ? type
                : droppedItem.type;

        if(droppedCategoryType === 'controller'){
            dispatch(setSelectedDevicePosition({ x: normalizedDropPosition.x, y: normalizedDropPosition.y }));
            dispatch(setSelectingDevice(true))
            setEasyModeDropPopup(null);
            return;
        }

        if(droppedCategoryType === 'detector'){
            const template = getDetectorTemplate({ title: droppedItem.title, icon: droppedItem.icon.replace('controllers/black/', '') });
            const newDetectorId = nextDroppedDetectorIdRef.current;
            nextDroppedDetectorIdRef.current += 1;

            const newDetector: DroppedFloorplanItem = {
                id: newDetectorId,
                icon: template?.refImg ?? droppedItem.icon.replace('controllers/black/', ''),
                title: template?.name ?? droppedItem.title,
                state: 'error',
                x: normalizedDropPosition.x,
                y: normalizedDropPosition.y,
                type: 'detector',
                applianceType: ''
            };

            setDroppedDetectors((currentItems) => [...currentItems, newDetector]);
            setEasyModeDropPopup(null);
            checkIfShouldShowAddDevicePopup();
            return;
        }

        const newItemId = droppedCategoryType === 'actuator'
            ? nextDroppedActuatorIdRef.current
            : nextDroppedApplianceIdRef.current;

        if (droppedCategoryType === 'actuator') {
            nextDroppedActuatorIdRef.current += 1;
        } else {
            nextDroppedApplianceIdRef.current += 1;
        }

        const newItem: DroppedFloorplanItem & { refImg: string } = {
            id: newItemId,
            icon: droppedItem.icon.replace('controllers/black/', ''),
            title: droppedItem.title,
            state: state || 'error',
            x: normalizedDropPosition.x,
            y: normalizedDropPosition.y,
            type: droppedCategoryType === 'actuator' ? 'actuator' : 'appliance',
            applianceType: applianceType || '',
            dinrail: droppedItem.dinrail || false,
            refImg: ''
        };

        if (droppedCategoryType === 'actuator') {
            const template = getActuatorTemplate({
                type: droppedItem.type,
                title: droppedItem.title,
                icon: droppedItem.icon
            });
            if (template?.type) {
                newItem.modelType = template.type;
            }
            if (template?.dinrail) {
                newItem.dinrail = true;
            }
            if (template?.refImg) {
                newItem.refImg = template.refImg;
            }
            if (newItem.dinrail) {
                dispatch(setBottomBarOpen(true));
            }
            setDroppedActuators((currentItems) => [...currentItems, newItem]);
            setEasyModeDropPopup(null);
        } else {
            setDroppedItems((currentItems) => [...currentItems, newItem]);

            const easyModeItems = newItem.applianceType in applianceTypes
                ? applianceTypes[newItem.applianceType as ApplianceTypeKey]
                : undefined;

            const isActuatorsEasyMode = getFromLocalStorage('actuatorsEasyMode') === 'true';
            if (isActuatorsEasyMode) {
                const position = { x: Math.min(100, normalizedDropPosition.x - 12.5), y: Math.min(100, normalizedDropPosition.y - 15) };
                setEasyModeDropPopup({
                    applianceId: newItemId,
                    x: position.x,
                    y: position.y,
                    items: easyModeItems
                });
                setEasyModePopupStep('selectActuator');
                setEasyModeSelectedActuator('');
            } else {
                setEasyModeDropPopup(null);
                setEasyModePopupStep('selectActuator');
                setEasyModeSelectedActuator('');
            }
        }

        checkIfShouldShowAddDevicePopup();
    };

    useEffect(() => {
        dispatch(
            setAppliances(
                droppedItems.map((item) => {
                    const existing = appliancesRef.current.find((a) => a.id === item.id);
                    return {
                        id: item.id,
                        icon: item.icon,
                        type: item.type,
                        applianceType: item.applianceType,
                        label: existing?.label ?? item.title,
                        state: item.state,
                        x: item.x,
                        y: item.y
                    };
                })
            )
        );
    }, [dispatch, droppedItems]);

    useEffect(() => {
        dispatch(
            setActuators([
                ...actuatorsRef.current.filter((a) => a.builtIn),
                ...droppedActuators.map((item) => {
                    const existing = actuatorsRef.current.find((a) => a.id === item.id);
                    const template = getActuatorTemplate(item);
                    return {
                        tech: item.tech? item.tech : template?.tech ?? existing?.tech ?? '',
                        id: item.id,
                        icon: item.icon,
                        type: template?.type ?? existing?.type ?? item.modelType ?? item.type,
                        applianceType: template?.applianceType ?? item.applianceType,
                        name: existing?.name ?? template?.name ?? item.title,
                        refImg: existing?.refImg ?? template?.refImg ?? '',
                        dinrail: item.dinrail ?? existing?.dinrail ?? template?.dinrail ?? false,
                        x: item.x,
                        y: item.y,
                        numberOfChannels: template?.numberOfChannels ?? existing?.numberOfChannels ?? existing?.maximumApplianceConnections ?? 1,
                        maximumApplianceConnections: template?.numberOfChannels ?? existing?.maximumApplianceConnections ?? 1,
                        maximumAppliancesPerChannel: template?.maximumAppliancesPerChannel ?? existing?.maximumAppliancesPerChannel,
                        linkedControllerId: existing?.linkedControllerId,
                        price: template?.price ?? existing?.price
                    };
                })
            ])
        );
    }, [dispatch, droppedActuators, getActuatorTemplate]);

    useEffect(() => {
        dispatch(
            setDetectors(
                droppedDetectors.map((item) => {
                    const existing = detectorsRef.current.find((d) => d.id === item.id);
                    const template = getDetectorTemplate(item);
                    return {
                        tech: template?.tech ?? existing?.tech ?? '',
                        id: item.id,
                        type: 'detector',
                        name: existing?.name ?? template?.name ?? item.title,
                        refImg: existing?.refImg ?? template?.refImg ?? item.icon,
                        icon: item.icon,
                        x: item.x,
                        y: item.y,
                        state: item.state,
                        connectedActuatorIds: existing?.connectedActuatorIds ?? [],
                        price: template?.price ?? existing?.price
                    };
                })
            )
        );
    }, [dispatch, droppedDetectors, getDetectorTemplate]);


    useEffect(() => {
        setDroppedItems([]);
        setDroppedActuators([]);
        setDroppedDetectors([]);
        nextDroppedApplianceIdRef.current = 1;
        nextDroppedActuatorIdRef.current = 1;
        nextDroppedDetectorIdRef.current = 1;
        prevDroppedItemIdsRef.current = new Set();
        hasHydratedAppliancesRef.current = false;
        hasHydratedActuatorsRef.current = false;
        hasHydratedDetectorsRef.current = false;
        setControllerPositions({});
        setActiveDraggedItem(null);
        setDragCursorPosition(null);
        setActivePointerType(null);
        activeTouchIdentifierRef.current = null;
        setEasyModeDropPopup(null);
        setEasyModePopupStep('selectActuator');
        setEasyModeSelectedActuator('');
    }, [configurationResetRevision]);

    useEffect(() => {
        if (!easyModeDropPopup) {
            return;
        }

        const exists = droppedItems.some((item) => item.id === easyModeDropPopup.applianceId);
        if (!exists) {
            setEasyModeDropPopup(null);
            setEasyModePopupStep('selectActuator');
            setEasyModeSelectedActuator('');
        }
    }, [droppedItems, easyModeDropPopup]);

    useEffect(() => {
        if (hasHydratedAppliancesRef.current || appliances.length === 0) {
            return;
        }

        const hydratedItems = appliances.map((appliance: ApplianceToHydrate) => ({
            id: appliance.id,
            icon: appliance.icon,
            title: appliance.label,
            type: 'appliance',
            applianceType: appliance.applianceType,
            state: appliance.state ?? 'error',
            connectedTo: appliance.connectedTo,
            x: typeof appliance.x === 'number' ? appliance.x : 50,
            y: typeof appliance.y === 'number' ? appliance.y : 50
        }));

        setDroppedItems(hydratedItems);
        nextDroppedApplianceIdRef.current = Math.max(...hydratedItems.map((item) => item.id), 0) + 1;
        prevDroppedItemIdsRef.current = new Set(hydratedItems.map((item) => item.id));
        hasHydratedAppliancesRef.current = true;
    }, [appliances]);

    useEffect(() => {
        if (hasHydratedActuatorsRef.current || actuators.length === 0) {
            return;
        }

        // Built-in actuators are not placed on the floorplan; skip them during hydration.
        const nonBuiltInActuators = actuators.filter((actuator) => !actuator.builtIn);

        if (nonBuiltInActuators.length === 0) {
            hasHydratedActuatorsRef.current = true;
            return;
        }

        const hydratedActuators = nonBuiltInActuators.map((actuator) => ({
            id: actuator.id ?? 0,
            icon: actuator.icon,
            title: actuator.name,
            type: 'actuator',
            modelType: actuator.type,
            applianceType: actuator.applianceType,
            state: actuator.state ?? 'error',
            connectedAppliances: actuator.connectedAppliances,
            connectedAppliancesByChannel: actuator.connectedAppliancesByChannel,
            dinrail: actuator.dinrail ?? false,
            x: typeof actuator.x === 'number' ? actuator.x : 50,
            y: typeof actuator.y === 'number' ? actuator.y : 50
        }));

        setDroppedActuators(hydratedActuators);
        hasHydratedActuatorsRef.current = true;
    }, [actuators]);

    useEffect(() => {
        if (!hasHydratedActuatorsRef.current) {
            return;
        }

        setDroppedActuators((currentItems) => {
            const nextItems = currentItems.filter((item) => actuators.some((actuator) => actuator.id === item.id && !actuator.builtIn));

            if (nextItems.length === currentItems.length) {
                return currentItems;
            }

            return nextItems;
        });
    }, [actuators]);

    useEffect(() => {
        // Keep the ID counter ahead of every actuator, including built-in ones added after initial hydration.
        const maxActuatorId = Math.max(...actuators.map((actuator) => actuator.id ?? 0), 0);
        nextDroppedActuatorIdRef.current = Math.max(nextDroppedActuatorIdRef.current, maxActuatorId + 1);
    }, [actuators]);

    useEffect(() => {
        if (hasHydratedDetectorsRef.current || detectors.length === 0) {
            return;
        }

        const hydratedDetectors = detectors.map((detector) => ({
            id: detector.id ?? 0,
            icon: detector.refImg,
            title: detector.name,
            type: 'detector',
            applianceType: '',
            state: detector.state ?? 'error',
            x: typeof detector.x === 'number' ? detector.x : 50,
            y: typeof detector.y === 'number' ? detector.y : 50
        }));

        setDroppedDetectors(hydratedDetectors);
        nextDroppedDetectorIdRef.current = Math.max(...hydratedDetectors.map((item) => item.id), 0) + 1;
        hasHydratedDetectorsRef.current = true;
    }, [detectors]);

    useEffect(() => {
        if (!hasHydratedDetectorsRef.current) {
            return;
        }

        setDroppedDetectors((currentItems) => {
            const nextItems = currentItems.filter((item) => detectors.some((detector) => detector.id === item.id));

            if (nextItems.length === currentItems.length) {
                return currentItems;
            }

            return nextItems;
        });
    }, [detectors]);

    useEffect(() => {
        const maxDetectorId = Math.max(...detectors.map((detector) => detector.id ?? 0), 0);
        nextDroppedDetectorIdRef.current = Math.max(nextDroppedDetectorIdRef.current, maxDetectorId + 1);
    }, [detectors]);

    useEffect(() => {
        const actuatorSyncSignature = JSON.stringify(
            actuators.map((actuator) => ({
                id: actuator.id,
                connectedAppliancesByChannel: actuator.connectedAppliancesByChannel ?? [],
                connectedAppliances: actuator.connectedAppliances ?? []
            }))
        );

        if (lastActuatorSyncSignatureRef.current === actuatorSyncSignature) {
            return;
        }

        lastActuatorSyncSignatureRef.current = actuatorSyncSignature;
        dispatch(syncControllerChannelConnections(actuators));
    }, [actuators, dispatch]);

    useEffect(() => {
        const controllerSyncSignature = JSON.stringify(
            controllers.map((controller) => ({
                id: controller.id,
                iconsData: controller.iconsData.map((slot) => ({
                    applianceId: slot.applianceId ?? [],
                    connectedActuatorChannels: slot.connectedActuatorChannels ?? [],
                    connectedActuatorId: slot.connectedActuatorId,
                    connectedActuatorChannel: slot.connectedActuatorChannel
                }))
            }))
        );

        if (lastControllerSyncSignatureRef.current === controllerSyncSignature) {
            return;
        }

        lastControllerSyncSignatureRef.current = controllerSyncSignature;
        dispatch(syncApplianceControllerConnections(controllers));
        dispatch(setActuatorControllerLinks(controllers));
    }, [controllers, dispatch]);

    useEffect(() => {
        const prevIds = prevDroppedItemIdsRef.current;
        const nextIds = new Set(droppedItems.map((item) => item.id));
        const removedApplianceIds: number[] = [];

        prevIds.forEach((prevId) => {
            if(!nextIds.has(prevId)){
                removedApplianceIds.push(prevId);
                dispatch(removeApplianceFromAllControllers(prevId));
            }
        });

        if (removedApplianceIds.length > 0) {
            const removedApplianceIdSet = new Set(removedApplianceIds);
            let hasActuatorChanges = false;

            const nextActuators = actuatorsRef.current.map((actuator) => {
                const nextActuator = removeAppliancesFromActuator(actuator, removedApplianceIdSet);
                const hasChanged = JSON.stringify(nextActuator.connectedAppliancesByChannel ?? [])
                    !== JSON.stringify(actuator.connectedAppliancesByChannel ?? [])
                    || (nextActuator.connectedAppliances ?? []).length !== (actuator.connectedAppliances ?? []).length;

                if (!hasChanged) {
                    return actuator;
                }

                hasActuatorChanges = true;
                return nextActuator;
            });

            if (hasActuatorChanges) {
                dispatch(setActuators(nextActuators));
            }
        }

        prevDroppedItemIdsRef.current = nextIds;
    }, [dispatch, droppedItems]);

    useEffect(() => {
        setControllerPositions((currentPositions) => {
            const nextPositions: Record<number, NormalizedPosition> = { ...currentPositions };

            const controllerIds = new Set(controllers.map((controller) => controller.id));

            Object.keys(nextPositions).forEach((id) => {
                const numericId = Number(id);

                if (!controllerIds.has(numericId)) {
                    delete nextPositions[numericId];
                }
            });

            controllers.forEach((controller, index) => {
                if (!nextPositions[controller.id]) {
                    const offset = (Object.keys(nextPositions).length + index) % 10;
                    const x = typeof controller.x === 'number' ? controller.x : 15 + offset * 7;
                    const y = typeof controller.y === 'number' ? controller.y : 15 + offset * 7;

                    nextPositions[controller.id] = {
                        x,
                        y
                    };

                    if (typeof controller.x !== 'number' || typeof controller.y !== 'number') {
                        dispatch(setDevicePosition({ id: controller.id, x, y }));
                    }
                }
            });

            return nextPositions;
        });
    }, [controllers, dispatch]);

    const handlePlacedItemMouseDown = (event: MouseEvent<HTMLDivElement>, item: DroppedFloorplanItem, itemIcon: string) => {
        event.preventDefault();
        setActivePointerType('mouse');
        setActiveDraggedItem({
            kind: item.type as 'appliance' | 'actuator' | 'detector',
            id: item.id,
            title: item.title,
            imgSrc: itemIcon
        });
        setDragCursorPosition({ x: event.clientX, y: event.clientY });
    };

    const handlePlacedItemTouchStart = (event: ReactTouchEvent<HTMLDivElement>, item: DroppedFloorplanItem) => {
        event.preventDefault();

        const touchPoint = event.changedTouches[0];

        if (!touchPoint) {
            return;
        }

        activeTouchIdentifierRef.current = touchPoint.identifier;
        setActivePointerType('touch');
        setActiveDraggedItem({
            kind: item.type as 'appliance' | 'actuator' | 'detector',
            id: item.id,
            title: item.title,
            imgSrc: item.type === 'detector'
                ? addPublicURL(`assets/detectors/${item.icon}`)
                : getIconPath(item.icon)
        });
        setDragCursorPosition({ x: touchPoint.clientX, y: touchPoint.clientY });
    };

    const handleControllerMouseDown = (event: MouseEvent<HTMLDivElement>, controllerId: number, title: string, previewImage: string, color: string) => {
        event.preventDefault();
        setActivePointerType('mouse');
        setActiveDraggedItem({
            kind: 'controller',
            id: controllerId,
            title,
            imgSrc: addPublicURL(`/assets/controllers/preview/${previewImage + '-' + color}.png`)
        });
        setDragCursorPosition({ x: event.clientX, y: event.clientY });
    };

    const handleControllerTouchStart = (event: ReactTouchEvent<HTMLDivElement>, controllerId: number, title: string, previewImage: string) => {
        event.preventDefault();

        const touchPoint = event.changedTouches[0];

        if (!touchPoint) {
            return;
        }

        activeTouchIdentifierRef.current = touchPoint.identifier;
        setActivePointerType('touch');
        setActiveDraggedItem({
            kind: 'controller',
            id: controllerId,
            title,
            imgSrc: addPublicURL(`/assets/controllers/preview/${previewImage}.png`)
        });
        setDragCursorPosition({ x: touchPoint.clientX, y: touchPoint.clientY });
    };

    useEffect(() => {
        if (!activeDraggedItem || activePointerType !== 'mouse') {
            return;
        }

        const handleMouseMove = (event: globalThis.MouseEvent) => {
            setDragCursorPosition({ x: event.clientX, y: event.clientY });
        };

        const handleMouseUp = (event: globalThis.MouseEvent) => {
            const normalizedDropPosition = getNormalizedDropPosition(event.clientX, event.clientY);

            if (normalizedDropPosition?.isInside) {
                if (activeDraggedItem.kind === 'appliance') {
                    setDroppedItems((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                } else if (activeDraggedItem.kind === 'actuator') {
                    setDroppedActuators((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                        dispatch(setActuatorPosition({
                            id: activeDraggedItem.id,
                            x: normalizedDropPosition.x,
                            y: normalizedDropPosition.y
                        }));
                } else if (activeDraggedItem.kind === 'detector') {
                    setDroppedDetectors((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                } else {
                    dispatch(setDevicePosition({
                        id: activeDraggedItem.id,
                        x: normalizedDropPosition.x,
                        y: normalizedDropPosition.y
                    }));
                    setControllerPositions((currentPositions) => ({
                        ...currentPositions,
                        [activeDraggedItem.id]: {
                            x: normalizedDropPosition.x,
                            y: normalizedDropPosition.y
                        }
                    }));
                }
            } else {
                if (activeDraggedItem.kind === 'appliance') {
                    setDroppedItems((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );
                } else if (activeDraggedItem.kind === 'actuator') {
                    setDroppedActuators((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );

                    dispatch(setAppliances(
                        appliances.map((appliance) => {
                            if ((appliance).actuator === activeDraggedItem.id) {
                                const { ...rest } = appliance;
                                return { ...rest, actuator: undefined };
                            }
                            return appliance;
                        })
                    ));
                } else if (activeDraggedItem.kind === 'detector') {
                    setDroppedDetectors((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );
                } else {
                    deleteController(activeDraggedItem.id);
                    setControllerPositions((currentPositions) => {
                        const nextPositions = { ...currentPositions };
                        delete nextPositions[activeDraggedItem.id];
                        return nextPositions;
                    });
                }
            }

            setActiveDraggedItem(null);
            setDragCursorPosition(null);
            setActivePointerType(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp, { once: true });

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [activeDraggedItem, activePointerType, deleteController, appliances, dispatch]);

    useEffect(() => {
        if (!activeDraggedItem || activePointerType !== 'touch') {
            return;
        }

        const getTrackedTouch = (touchList: TouchList) => {
            const activeIdentifier = activeTouchIdentifierRef.current;

            if (activeIdentifier === null) {
                return null;
            }

            for (let index = 0; index < touchList.length; index += 1) {
                const touchPoint = touchList.item(index);

                if (touchPoint && touchPoint.identifier === activeIdentifier) {
                    return touchPoint;
                }
            }

            return null;
        };

        const handleTouchMove = (event: globalThis.TouchEvent) => {
            const trackedTouch = getTrackedTouch(event.touches);

            if (!trackedTouch) {
                return;
            }

            event.preventDefault();
            setDragCursorPosition({ x: trackedTouch.clientX, y: trackedTouch.clientY });
        };

        const completeTouchDrag = (event: globalThis.TouchEvent) => {
            const trackedTouch = getTrackedTouch(event.changedTouches);

            if (!trackedTouch) {
                return;
            }

            const normalizedDropPosition = getNormalizedDropPosition(trackedTouch.clientX, trackedTouch.clientY);

            if (normalizedDropPosition?.isInside) {
                if (activeDraggedItem.kind === 'appliance') {
                    setDroppedItems((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                } else if (activeDraggedItem.kind === 'actuator') {
                    setDroppedActuators((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                        dispatch(setActuatorPosition({
                            id: activeDraggedItem.id,
                            x: normalizedDropPosition.x,
                            y: normalizedDropPosition.y
                        }));
                } else if (activeDraggedItem.kind === 'detector') {
                    setDroppedDetectors((currentItems) =>
                        currentItems.map((item) =>
                            item.id === activeDraggedItem.id
                                ? {
                                    ...item,
                                    x: normalizedDropPosition.x,
                                    y: normalizedDropPosition.y
                                }
                                : item
                        )
                    );
                } else {
                    dispatch(setDevicePosition({
                        id: activeDraggedItem.id,
                        x: normalizedDropPosition.x,
                        y: normalizedDropPosition.y
                    }));
                    setControllerPositions((currentPositions) => ({
                        ...currentPositions,
                        [activeDraggedItem.id]: {
                            x: normalizedDropPosition.x,
                            y: normalizedDropPosition.y
                        }
                    }));
                }
            } else {
                if (activeDraggedItem.kind === 'appliance') {
                    setDroppedItems((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );
                } else if (activeDraggedItem.kind === 'actuator') {
                    setDroppedActuators((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );
                } else if (activeDraggedItem.kind === 'detector') {
                    setDroppedDetectors((currentItems) =>
                        currentItems.filter((item) => item.id !== activeDraggedItem.id)
                    );
                } else {
                    deleteController(activeDraggedItem.id);
                    setControllerPositions((currentPositions) => {
                        const nextPositions = { ...currentPositions };
                        delete nextPositions[activeDraggedItem.id];
                        return nextPositions;
                    });
                }
            }

            activeTouchIdentifierRef.current = null;
            setActiveDraggedItem(null);
            setDragCursorPosition(null);
            setActivePointerType(null);
        };

        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', completeTouchDrag, { passive: false });
        window.addEventListener('touchcancel', completeTouchDrag, { passive: false });

        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', completeTouchDrag);
            window.removeEventListener('touchcancel', completeTouchDrag);
        };
    }, [activeDraggedItem, activePointerType, deleteController, dispatch]);

    const getSlotLinks = useCallback((slot: IconsI): ConnectedActuatorChannelI[] => {
        const multiLinks = slot.connectedActuatorChannels ?? [];
        const legacyLink =
            slot.connectedActuatorId !== undefined && slot.connectedActuatorChannel !== undefined
                ? [{ actuatorId: slot.connectedActuatorId, channel: slot.connectedActuatorChannel }]
                : [];

        return (multiLinks.length > 0 ? multiLinks : legacyLink)
            .filter((link, index, array) =>
                array.findIndex((candidate) =>
                    candidate.actuatorId === link.actuatorId && candidate.channel === link.channel
                ) === index
            );
    }, []);

    const getControllerLinkedActuatorIds = useCallback((controller: DeviceI) => {
        return new Set(
            controller.iconsData
                .flatMap((slot) => getSlotLinks(slot))
                .map((link) => link.actuatorId)
        );
    }, [getSlotLinks]);

    const getControllerConnectedActuatorIds = useCallback((controller: DeviceI) => {
        const actuatorIds = getControllerLinkedActuatorIds(controller);
        if (
            controller.type === 'thermostat'
            && controller.thermostatRequiredActuator !== null
            && controller.thermostatRequiredActuator !== undefined
            && (controller.connectedActuatorIds ?? [controller.connectedActuator]).some((actuatorId) => actuatorId !== undefined)
        ) {
            (controller.connectedActuatorIds ?? [controller.connectedActuator]).forEach((actuatorId) => {
                if (actuatorId !== undefined) {
                    actuatorIds.add(actuatorId);
                }
            });
        }

        return actuatorIds;
    }, [getControllerLinkedActuatorIds]);

    const getControllerAssignedSlotsCount = useCallback((controller: DeviceI) => {
        return controller.iconsData.filter((slot) => {
            return getSlotLinks(slot).length > 0 || (slot.applianceId ?? []).length > 0;
        }).length;
    }, [getSlotLinks]);

    const getControllerIdsForActuator = useCallback((actuatorId: number) => {
        return controllers
            .filter((controller) =>
                getControllerConnectedActuatorIds(controller).has(actuatorId)
            )
            .map((controller) => controller.id);
    }, [controllers, getControllerConnectedActuatorIds]);

    const getDetectorIdsForActuator = useCallback((actuatorId: number) => {
        return detectors
            .filter((detector) => getDetectorConnectedActuatorIds(detector).includes(actuatorId))
            .map((detector) => detector.id as number);
    }, [detectors]);

    const isMenuHoveredItem = hoveredItem?.source === 'menu';

    const highlightedControllerIds = useMemo(() => {
        if (isMenuHoveredItem) {
            return new Set<number>();
        }

        if (hoveredItem?.kind === 'appliance') {
            const applianceId = hoveredItem.id;
            const slotLinkedControllerIds = controllers
                .filter((controller) => controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(applianceId)))
                .map((controller) => controller.id);
            const actuatorLinkedControllerIds = controllers
                .filter((controller) =>
                    Array.from(getControllerConnectedActuatorIds(controller)).some((actuatorId) =>
                        isApplianceConnectedToActuator(
                            actuators.find((actuator) => actuator.id === actuatorId),
                            applianceId
                        )
                    )
                )
                .map((controller) => controller.id);
            // Built-in actuators connect their appliances straight to the owning controller.
            const builtInLinkedControllerIds = actuators
                .filter((actuator) => actuator.builtIn && actuator.linkedControllerId !== undefined && isApplianceConnectedToActuator(actuator, applianceId))
                .map((actuator) => actuator.linkedControllerId as number);
            return new Set([...slotLinkedControllerIds, ...actuatorLinkedControllerIds, ...builtInLinkedControllerIds]);
        }

        if (hoveredItem?.kind === 'actuator') {
            const actuator = actuators.find((item) => item.id === hoveredItem.id);
            if (!actuator) {
                return new Set<number>();
            }

            const connectedApplianceIds = new Set(getConnectedApplianceIds(actuator));
            const directlyLinkedControllerIds = getControllerIdsForActuator(hoveredItem.id);
            const applianceLinkedControllerIds = controllers
                .filter((controller) => controller.iconsData.some((slot) => (slot.applianceId ?? []).some((applianceId) => connectedApplianceIds.has(applianceId))))
                .map((controller) => controller.id);

            return new Set(
                [...directlyLinkedControllerIds, ...applianceLinkedControllerIds]
            );
        }

        return new Set<number>();
    }, [actuators, controllers, getControllerConnectedActuatorIds, getControllerIdsForActuator, hoveredItem, isMenuHoveredItem]);

    const highlightedApplianceIds = useMemo(() => {
        if (isMenuHoveredItem) {
            return new Set<number>();
        }

        if (hoveredItem?.kind === 'controller') {
            const controller = controllers.find((item) => item.id === hoveredItem.id);
            if (!controller) {
                return new Set<number>();
            }
            const slotLinkedApplianceIds = controller.iconsData.flatMap((slot) => slot.applianceId ?? []);
            const requiredActuatorApplianceIds = Array.from(getControllerConnectedActuatorIds(controller))
                .flatMap((actuatorId) => getConnectedApplianceIds(actuators.find((actuator) => actuator.id === actuatorId)));
            // Built-in actuators connect their appliances straight to the owning controller.
            const builtInActuator = actuators.find((actuator) => actuator.builtIn && actuator.linkedControllerId === controller.id);
            const builtInLinkedApplianceIds = builtInActuator ? getConnectedApplianceIds(builtInActuator) : [];
            return new Set([...slotLinkedApplianceIds, ...requiredActuatorApplianceIds, ...builtInLinkedApplianceIds]);
        }

        if (hoveredItem?.kind === 'actuator') {
            const actuator = actuators.find((a) => a.id === hoveredItem.id);
            return new Set<number>(getConnectedApplianceIds(actuator));
        }

        if (hoveredItem?.kind === 'detector') {
            const detector = detectors.find((item) => item.id === hoveredItem.id);
            return new Set(
                getDetectorConnectedActuatorIds(detector).flatMap((actuatorId) =>
                    getConnectedApplianceIds(actuators.find((item) => item.id === actuatorId))
                )
            );
        }

        return new Set<number>();
    }, [controllers, actuators, detectors, getControllerConnectedActuatorIds, hoveredItem, isMenuHoveredItem]);

    const highlightedActuatorIds = useMemo(() => {
        if (isMenuHoveredItem) {
            return new Set<number>();
        }

        if (hoveredItem?.kind === 'appliance') {
            const applianceId = hoveredItem.id;
            return new Set(
                actuators
                    .filter((a) => isApplianceConnectedToActuator(a, applianceId))
                    .map((a) => a.id as number)
            );
        }

        if (hoveredItem?.kind === 'controller') {
            const controller = controllers.find((item) => item.id === hoveredItem.id);
            if (!controller) return new Set<number>();
            const directlyLinkedActuatorIds = Array.from(getControllerConnectedActuatorIds(controller));
            const connectedApplianceIds = new Set(
                controller.iconsData.flatMap((slot) => slot.applianceId ?? [])
            );
            return new Set<number>(
                [
                    ...directlyLinkedActuatorIds,
                    ...actuators
                    .filter((a) => getConnectedApplianceIds(a).some((appId) => connectedApplianceIds.has(appId)))
                    .map((a) => a.id as number)
                ]
            );
        }

        if (hoveredItem?.kind === 'detector') {
            const detector = detectors.find((item) => item.id === hoveredItem.id);
            return new Set<number>(getDetectorConnectedActuatorIds(detector));
        }

        return new Set<number>();
    }, [actuators, controllers, detectors, getControllerConnectedActuatorIds, hoveredItem, isMenuHoveredItem]);

    const highlightedDetectorIds = useMemo(() => {
        if (isMenuHoveredItem) {
            return new Set<number>();
        }

        if (hoveredItem?.kind === 'actuator') {
            const actuatorId = hoveredItem.id;
            return new Set<number>(
                detectors
                    .filter((detector) => getDetectorConnectedActuatorIds(detector).includes(actuatorId))
                    .map((detector) => detector.id as number)
            );
        }

        if (hoveredItem?.kind === 'appliance') {
            const actuatorIds = actuators
                .filter((actuator) => isApplianceConnectedToActuator(actuator, hoveredItem.id))
                .map((actuator) => actuator.id as number);
            return new Set<number>(actuatorIds.flatMap((actuatorId) => getDetectorIdsForActuator(actuatorId)));
        }

        return new Set<number>();
    }, [actuators, detectors, getDetectorIdsForActuator, hoveredItem, isMenuHoveredItem]);

    const getItemHighlightClassName = (kind: HighlightKind, state: 'hovered' | 'linked') => {
        if (!showBackgroundColors) {
            return '';
        }

        if (state === 'hovered') {
            switch (kind) {
                case 'controller':
                    return styles.droppedItemHoveredController;
                case 'appliance':
                    return styles.droppedItemHoveredAppliance;
                case 'actuator':
                    return styles.droppedItemHoveredActuator;
                case 'detector':
                    return styles.droppedItemHoveredActuator;
                default:
                    return '';
            }
        }

        switch (kind) {
            case 'controller':
                return styles.droppedItemLinkedController;
            case 'appliance':
                return styles.droppedItemLinkedAppliance;
            case 'actuator':
                return styles.droppedItemLinkedActuator;
            case 'detector':
                return styles.droppedItemLinkedActuator;
            default:
                return '';
        }
    };

    const getConnectionLineClassName = (kind: LineKind) => {
        switch (kind) {
            case 'controller':
                return styles.connectionLineController;
            case 'appliance':
                return styles.connectionLineAppliance;
            case 'actuator':
                return styles.connectionLineActuator;
            case 'detector':
                return styles.connectionLineDetector;
            default:
                return styles.connectionLine;
        }
    };

    const getConnectionArrowClassName = (kind: LineKind) => {
        switch (kind) {
            case 'controller':
                return styles.connectionArrowController;
            case 'appliance':
                return styles.connectionArrowAppliance;
            case 'actuator':
                return styles.connectionArrowActuator;
            default:
                return styles.connectionArrow;
        }
    };

    const getStateBackgroundClassName = (itemState?: string) => {
        switch (itemState) {
            case 'warning':
                return styles.droppedItemWarning;
            case 'error':
                return styles.droppedItemError;
            default:
                return '';
        }
    };

    const getConnectionAnchorPoint = (point: { x: number; y: number; dinrail?: boolean } | undefined) => {
        if (!point) {
            return null;
        }

        if (point.dinrail) {
            return {
                x: point.x + 2.25,
                y: point.y + 2.25
            };
        }

        return {
            x: point.x,
            y: point.y
        };
    };

    const getMidpoint = (source: { x: number; y: number }, target: { x: number; y: number }) => ({
        x: (source.x + target.x) / 2,
        y: (source.y + target.y) / 2
    });

    const connectionEndpointRadiusPx = useMemo(() => {
        return Math.max(40, Math.min(60, dropZoneSize.width * 0.9 * 0.05)) / 2;
    }, [dropZoneSize.width]);

    const trimConnectionLineToCircleEdge = useCallback((line: { x1: number; y1: number; x2: number; y2: number; kind: LineKind }) => {
        const deltaXPx = ((line.x2 - line.x1) / 100) * dropZoneSize.width;
        const deltaYPx = ((line.y2 - line.y1) / 100) * dropZoneSize.height;
        const segmentLengthPx = Math.hypot(deltaXPx, deltaYPx);

        if (segmentLengthPx === 0) {
            return line;
        }

        const totalTrimPx = Math.min(connectionEndpointRadiusPx * 2, Math.max(0, segmentLengthPx - 1));
        const trimAtEachEndPx = totalTrimPx / 2;
        const unitXPx = deltaXPx / segmentLengthPx;
        const unitYPx = deltaYPx / segmentLengthPx;
        const startOffsetX = ((unitXPx * trimAtEachEndPx) / dropZoneSize.width) * 100;
        const startOffsetY = ((unitYPx * trimAtEachEndPx) / dropZoneSize.height) * 100;
        const endOffsetX = startOffsetX;
        const endOffsetY = startOffsetY;

        return {
            ...line,
            x1: line.x1 + startOffsetX,
            y1: line.y1 + startOffsetY,
            x2: line.x2 - endOffsetX,
            y2: line.y2 - endOffsetY,
        };
    }, [connectionEndpointRadiusPx, dropZoneSize.height, dropZoneSize.width]);

    const getDinrailActuatorsForAppliance = useCallback((applianceId: number) => {
        return droppedActuators
            .filter((item) => item.dinrail)
            .map((item) => ({
                droppedActuator: item,
                actuator: actuators.find((candidate) => candidate.id === item.id)
            }))
            .filter(({ actuator }) => isApplianceConnectedToActuator(actuator, applianceId));
    }, [actuators, droppedActuators]);

    const getControllerIdsForAppliance = useCallback((applianceId: number) => {
        return controllers
            .filter((controller) => controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(applianceId)))
            .map((controller) => controller.id);
    }, [controllers]);

    // Built-in actuators live on their controller, so they connect appliances straight to the controller position.
    const getBuiltInActuatorForController = useCallback((controllerId: number) => {
        return actuators.find((actuator) => actuator.builtIn && actuator.linkedControllerId === controllerId);
    }, [actuators]);

    const getBuiltInActuatorControllerIdsForAppliance = useCallback((applianceId: number) => {
        return actuators
            .filter((actuator) => actuator.builtIn && actuator.linkedControllerId !== undefined && isApplianceConnectedToActuator(actuator, applianceId))
            .map((actuator) => actuator.linkedControllerId as number);
    }, [actuators]);

    const currentDinrailMarkers = useMemo(() => {
        if (tieLinesMode === 'never') {
            return [] as DinrailConnectionMarker[];
        }

        if (tieLinesMode === 'always') {
            const markers: DinrailConnectionMarker[] = [];

            controllers.forEach((controller) => {
                const controllerPosition = controllerPositions[controller.id];
                if (!controllerPosition) {
                    return;
                }

                const applianceIds = new Set(controller.iconsData.flatMap((slot) => slot.applianceId ?? []));

                applianceIds.forEach((applianceId) => {
                    const appliance = droppedItems.find((item) => item.id === applianceId);
                    if (!appliance) {
                        return;
                    }

                    const dinrailActuators = getDinrailActuatorsForAppliance(appliance.id);
                    dinrailActuators.forEach(({ droppedActuator }) => {
                        const midpoint = getMidpoint(appliance, controllerPosition);
                        markers.push({
                            key: `${droppedActuator.id}:${appliance.id}:${controller.id}`,
                            actuatorId: droppedActuator.id,
                            applianceId: appliance.id,
                            controllerId: controller.id,
                            x: midpoint.x,
                            y: midpoint.y,
                            icon: droppedActuator.icon,
                            title: droppedActuator.title
                        });
                    });

                    if (!showActuators) {
                        const hiddenActuators = droppedActuators.filter((item) => {
                            if (item.dinrail) {
                                return false;
                            }
                            const actuator = actuators.find((candidate) => candidate.id === item.id);
                            return isApplianceConnectedToActuator(actuator, appliance.id);
                        });

                        hiddenActuators.forEach((hiddenActuator) => {
                            const midpoint = getMidpoint(appliance, controllerPosition);
                            markers.push({
                                key: `hidden:${hiddenActuator.id}:${appliance.id}:${controller.id}`,
                                actuatorId: hiddenActuator.id,
                                applianceId: appliance.id,
                                controllerId: controller.id,
                                x: midpoint.x,
                                y: midpoint.y,
                                icon: hiddenActuator.icon,
                                title: hiddenActuator.title
                            });
                        });
                    }
                });
            });

            return markers;
        }

        if (!hoveredItem) {
            return [] as DinrailConnectionMarker[];
        }

        if (isMenuHoveredItem) {
            return [] as DinrailConnectionMarker[];
        }

        const markers: DinrailConnectionMarker[] = [];

        if (hoveredItem.kind === 'appliance') {
            const appliance = droppedItems.find((item) => item.id === hoveredItem.id);
            if (!appliance) {
                return markers;
            }

            const controllerIds = getControllerIdsForAppliance(appliance.id);
            const dinrailActuators = getDinrailActuatorsForAppliance(appliance.id);

            controllerIds.forEach((controllerId) => {
                const controllerPosition = controllerPositions[controllerId];
                if (!controllerPosition) {
                    return;
                }

                dinrailActuators.forEach(({ droppedActuator }) => {
                    const midpoint = getMidpoint(appliance, controllerPosition);
                    markers.push({
                        key: `${droppedActuator.id}:${appliance.id}:${controllerId}`,
                        actuatorId: droppedActuator.id,
                        applianceId: appliance.id,
                        controllerId,
                        x: midpoint.x,
                        y: midpoint.y,
                        icon: droppedActuator.icon,
                        title: droppedActuator.title
                    });
                });

                if (!showActuators) {
                    const hiddenActuators = droppedActuators.filter((item) => {
                        if (item.dinrail) {
                            return false;
                        }
                        const actuator = actuators.find((candidate) => candidate.id === item.id);
                        return isApplianceConnectedToActuator(actuator, appliance.id);
                    });

                    hiddenActuators.forEach((hiddenActuator) => {
                        const midpoint = getMidpoint(appliance, controllerPosition);
                        markers.push({
                            key: `hidden:${hiddenActuator.id}:${appliance.id}:${controllerId}`,
                            actuatorId: hiddenActuator.id,
                            applianceId: appliance.id,
                            controllerId,
                            x: midpoint.x,
                            y: midpoint.y,
                            icon: hiddenActuator.icon,
                            title: hiddenActuator.title
                        });
                    });
                }
            });
        } else if (hoveredItem.kind === 'controller') {
            const controller = controllers.find((item) => item.id === hoveredItem.id);
            const controllerPosition = controllerPositions[hoveredItem.id];
            if (!controller || !controllerPosition) {
                return markers;
            }

            controller.iconsData
                .flatMap((slot) => slot.applianceId ?? [])
                .forEach((applianceId) => {
                    const appliance = droppedItems.find((item) => item.id === applianceId);
                    if (!appliance) {
                        return;
                    }

                    const dinrailActuators = getDinrailActuatorsForAppliance(applianceId);
                    dinrailActuators.forEach(({ droppedActuator }) => {
                        const midpoint = getMidpoint(controllerPosition, appliance);
                        markers.push({
                            key: `${droppedActuator.id}:${applianceId}:${controller.id}`,
                            actuatorId: droppedActuator.id,
                            applianceId,
                            controllerId: controller.id,
                            x: midpoint.x,
                            y: midpoint.y,
                            icon: droppedActuator.icon,
                            title: droppedActuator.title
                        });
                    });
                });
        }

        return markers;
    }, [controllers, controllerPositions, getControllerIdsForAppliance, getDinrailActuatorsForAppliance, hoveredItem, droppedItems, droppedActuators, actuators, showActuators, tieLinesMode, isMenuHoveredItem]);

    const connectionLines = useMemo(() => {
        if (tieLinesMode === 'never') {
            return [];
        }

        if (tieLinesMode === 'always') {
            const lines: { x1: number; y1: number; x2: number; y2: number; kind: LineKind }[] = [];

            const pushLine = (src: { x: number; y: number } | undefined, dst: { x: number; y: number } | undefined, kind: LineKind) => {
                if (src && dst) {
                    lines.push({ x1: src.x, y1: src.y, x2: dst.x, y2: dst.y, kind });
                }
            };

            // Always mode must keep actuator -> appliance links visible even if no controller exists.
            if (showActuators) {
                droppedActuators.forEach((droppedActuator) => {
                    if (droppedActuator.dinrail) {
                        return;
                    }

                    const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                    if (!actuatorAnchor) {
                        return;
                    }

                    const connectedApplianceIds = getConnectedApplianceIds(actuators.find((candidate) => candidate.id === droppedActuator.id));

                    connectedApplianceIds.forEach((applianceId) => {
                        const appliance = droppedItems.find((item) => item.id === applianceId);
                        if (!appliance) {
                            return;
                        }

                        pushLine(actuatorAnchor, appliance, 'appliance');

                        const controllerIds = getControllerIdsForAppliance(applianceId);
                        controllerIds.forEach((controllerId) => {
                            const controller = controllers.find((item) => item.id === controllerId);
                            const controllerPosition =
                                controllerPositions[controllerId]
                                ?? (
                                    controller && typeof controller.x === 'number' && typeof controller.y === 'number'
                                        ? { x: controller.x, y: controller.y }
                                        : { x: 50, y: 50 }
                                );

                            pushLine(controllerPosition, actuatorAnchor, 'actuator');
                        });
                    });
                });

            }

            droppedDetectors.forEach((droppedDetector) => {
                const detectorAnchor = getConnectionAnchorPoint(droppedDetector);
                if (!detectorAnchor) {
                    return;
                }

                const detector = detectors.find((candidate) => candidate.id === droppedDetector.id);
                getDetectorConnectedActuatorIds(detector).forEach((actuatorId) => {
                    const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                    if (!droppedActuator || droppedActuator.dinrail || !showActuators) {
                        return;
                    }

                    pushLine(
                        detectorAnchor,
                        getConnectionAnchorPoint(droppedActuator) ?? undefined,
                        'detector'
                    );
                });
            });

            controllers.forEach((controller) => {
                const controllerPosition =
                    controllerPositions[controller.id]
                    ?? (
                        typeof controller.x === 'number' && typeof controller.y === 'number'
                            ? { x: controller.x, y: controller.y }
                            : { x: 50, y: 50 }
                    );
                if (!controllerPosition) {
                    return;
                }

                if (showActuators) {
                    const linkedActuatorIds = getControllerConnectedActuatorIds(controller);

                    Array.from(linkedActuatorIds).forEach((actuatorId) => {
                        const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                        if (!droppedActuator || droppedActuator.dinrail) {
                            return;
                        }

                        const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                        pushLine(controllerPosition, actuatorAnchor ?? undefined, 'actuator');
                    });
                }

                // Built-in actuators have no floorplan anchor, so link straight from the controller.
                const builtInActuator = getBuiltInActuatorForController(controller.id);
                if (builtInActuator) {
                    getConnectedApplianceIds(builtInActuator).forEach((applianceId) => {
                        const appliance = droppedItems.find((item) => item.id === applianceId);
                        pushLine(controllerPosition, appliance, 'actuator');
                    });
                }

                const connectedApplianceIds = new Set(
                    controller.iconsData.flatMap((slot) => slot.applianceId ?? [])
                );
                const isDirectConnectThermostat = controller.type === 'thermostat'
                    && (controller.thermostatRequiredActuator === null || controller.thermostatRequiredActuator === undefined);

                connectedApplianceIds.forEach((applianceId) => {
                    const appliance = droppedItems.find((item) => item.id === applianceId);
                    if (!appliance) {
                        return;
                    }

                    if (isDirectConnectThermostat) {
                        pushLine(controllerPosition, appliance, 'controller');
                        return;
                    }

                    const dinrailActuators = getDinrailActuatorsForAppliance(applianceId);
                    const connectedNonDinrailActuators = droppedActuators.filter((item) => {
                        if (item.dinrail) {
                            return false;
                        }
                        const actuator = actuators.find((candidate) => candidate.id === item.id);
                        return isApplianceConnectedToActuator(actuator, applianceId);
                    });

                    if (showActuators && connectedNonDinrailActuators.length > 0) {
                        connectedNonDinrailActuators.forEach((droppedActuator) => {
                            const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                            pushLine(controllerPosition, actuatorAnchor ?? undefined, 'actuator');
                            pushLine(actuatorAnchor ?? undefined, appliance, 'appliance');
                        });
                        return;
                    }

                    // Fallback path for always mode: draw direct links when no visible actuator branch is present.
                    if (dinrailActuators.length > 0 || connectedNonDinrailActuators.length > 0) {
                        pushLine(appliance, controllerPosition, 'controller');
                    }
                });
            });

            return lines;
        }

        if (!hoveredItem) {
            return [];
        }

        if (isMenuHoveredItem) {
            return [];
        }

        // In Easy mode, keep controller and actuator tie lines visible for linked items.
        if (isEasyMode) {
            const lines: { x1: number; y1: number; x2: number; y2: number; kind: LineKind }[] = [];

            const pushLine = (src: { x: number; y: number }, dst: { x: number; y: number } | undefined, kind: LineKind) => {
                if (dst) lines.push({ x1: src.x, y1: src.y, x2: dst.x, y2: dst.y, kind });
            };

            if (hoveredItem.kind === 'appliance') {
                const src = droppedItems.find((i) => i.id === hoveredItem.id);
                if (!src) return [];
                const controllerIds = getControllerIdsForAppliance(src.id);
                const dinrailActuators = getDinrailActuatorsForAppliance(src.id);
                let hasVisibleActuatorPath = false;

                highlightedActuatorIds.forEach((id) => {
                    const droppedActuator = droppedActuators.find((i) => i.id === id);
                    if (!droppedActuator || droppedActuator.dinrail) {
                        return;
                    }

                    const actuator = getConnectionAnchorPoint(droppedActuator);
                    if (actuator) {
                        hasVisibleActuatorPath = true;
                        controllerIds.forEach((controllerId) => {
                            pushLine(controllerPositions[controllerId], actuator, 'actuator');
                        });

                        pushLine(actuator, src, 'appliance');
                    }
                });

                if (!hasVisibleActuatorPath && dinrailActuators.length > 0) {
                    controllerIds.forEach((controllerId) => {
                        pushLine(src, controllerPositions[controllerId], 'controller');
                    });
                }

                // Built-in actuators have no floorplan anchor, so link straight to the owning controller.
                getBuiltInActuatorControllerIdsForAppliance(src.id).forEach((controllerId) => {
                    pushLine(controllerPositions[controllerId], src, 'actuator');
                });

                highlightedDetectorIds.forEach((detectorId) => {
                    const detector = droppedDetectors.find((item) => item.id === detectorId);
                    if (!detector) {
                        return;
                    }

                    pushLine(src, detector, 'detector');
                });
            } else if (hoveredItem.kind === 'controller') {
                const src = controllerPositions[hoveredItem.id];
                if (!src) return [];
                const hoveredController = controllers.find((item) => item.id === hoveredItem.id);
                if (!hoveredController) return [];

                if (showActuators) {
                    const linkedActuatorIds = getControllerConnectedActuatorIds(hoveredController);

                    Array.from(linkedActuatorIds).forEach((actuatorId) => {
                        const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                        if (!droppedActuator || droppedActuator.dinrail) {
                            return;
                        }

                        const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                        pushLine(src, actuatorAnchor ?? undefined, 'actuator');
                    });
                }

                const builtInActuator = getBuiltInActuatorForController(hoveredController.id);
                const isDirectConnectThermostat = hoveredController.type === 'thermostat'
                    && (hoveredController.thermostatRequiredActuator === null || hoveredController.thermostatRequiredActuator === undefined);

                highlightedApplianceIds.forEach((id) => {
                    const appliance = droppedItems.find((i) => i.id === id);
                    if (!appliance) {
                        return;
                    }

                    if (isDirectConnectThermostat) {
                        pushLine(src, appliance, 'controller');
                        return;
                    }

                    const dinrailActuators = getDinrailActuatorsForAppliance(id);

                    if (dinrailActuators.length > 0) {
                        pushLine(src, appliance, 'appliance');
                    }

                    // Built-in actuator on this controller connects straight to the appliance.
                    if (builtInActuator && isApplianceConnectedToActuator(builtInActuator, id)) {
                        pushLine(src, appliance, 'actuator');
                    }

                    highlightedActuatorIds.forEach((actuatorId) => {
                        const actuator = actuators.find((a) => a.id === actuatorId);
                        const droppedActuator = droppedActuators.find((i) => i.id === actuatorId);
                        if (isApplianceConnectedToActuator(actuator, id) && droppedActuator && !droppedActuator.dinrail) {
                            const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                            if (!actuatorAnchor) {
                                return;
                            }

                            pushLine(actuatorAnchor, appliance, 'appliance');
                            pushLine(src, actuatorAnchor, 'actuator');
                        }
                    });
                });
            } else if (hoveredItem.kind === 'actuator') {
                const src = getConnectionAnchorPoint(droppedActuators.find((i) => i.id === hoveredItem.id));
                if (!src) return [];

                const directlyLinkedControllerIds = getControllerIdsForActuator(hoveredItem.id);
                directlyLinkedControllerIds.forEach((controllerId) => {
                    pushLine(controllerPositions[controllerId], src, 'actuator');
                });

                highlightedApplianceIds.forEach((id) => {
                    const appliance = droppedItems.find((i) => i.id === id);
                    if (appliance) {
                        pushLine(src, appliance, 'appliance');

                        const controllerIds = getControllerIdsForAppliance(id);
                        controllerIds.forEach((controllerId) => {
                            pushLine(controllerPositions[controllerId], src, 'actuator');
                        });
                    }
                });
            } else if (hoveredItem.kind === 'detector') {
                const detector = droppedDetectors.find((item) => item.id === hoveredItem.id);
                if (!detector) return [];

                getDetectorConnectedActuatorIds(detectors.find((item) => item.id === hoveredItem.id)).forEach((actuatorId) => {
                    const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                    if (!droppedActuator || droppedActuator.dinrail || !showActuators) {
                        return;
                    }

                    const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                    if (!actuatorAnchor) return;

                    pushLine(detector, actuatorAnchor, 'detector');
                    getConnectedApplianceIds(actuators.find((item) => item.id === actuatorId)).forEach((applianceId) => {
                        const appliance = droppedItems.find((item) => item.id === applianceId);
                        if (!appliance) return;

                        pushLine(actuatorAnchor, appliance, 'appliance');
                    });
                });
            }
            return lines;
        } else {
            const lines: { x1: number; y1: number; x2: number; y2: number; kind: LineKind }[] = [];

            const pushLine = (src: { x: number; y: number }, dst: { x: number; y: number } | undefined, kind: LineKind) => {
                if (dst) lines.push({ x1: src.x, y1: src.y, x2: dst.x, y2: dst.y, kind });
            };

            if (hoveredItem.kind === 'appliance') {
                const src = droppedItems.find((i) => i.id === hoveredItem.id);
                if (!src) return [];
                const controllerIds = getControllerIdsForAppliance(src.id);
                const dinrailActuators = getDinrailActuatorsForAppliance(src.id);
                const hasHiddenConnectedActuator = !showActuators && droppedActuators.some((item) => {
                    if (item.dinrail) {
                        return false;
                    }
                    const actuator = actuators.find((candidate) => candidate.id === item.id);
                    return isApplianceConnectedToActuator(actuator, src.id);
                });

                if (dinrailActuators.length > 0 || hasHiddenConnectedActuator) {
                    controllerIds.forEach((controllerId) => {
                        pushLine(src, controllerPositions[controllerId], 'controller');
                    });
                }

                // Built-in actuators have no floorplan anchor, so link straight to the owning controller.
                getBuiltInActuatorControllerIdsForAppliance(src.id).forEach((controllerId) => {
                    pushLine(controllerPositions[controllerId], src, 'actuator');
                });

                highlightedActuatorIds.forEach((actuatorId) => {
                    if (!showActuators) {
                        return;
                    }
                    const actuator = droppedActuators.find((i) => i.id === actuatorId);
                    if (!actuator || actuator.dinrail) {
                        return;
                    }

                    const actuatorAnchor = getConnectionAnchorPoint(actuator);
                    if (!actuatorAnchor) {
                        return;
                    }

                    const connectedControllerId = controllers.find((controller) =>
                        controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(hoveredItem.id))
                    )?.id;

                    if (connectedControllerId !== undefined) {
                        pushLine(controllerPositions[connectedControllerId], actuatorAnchor, 'actuator');
                    }

                    pushLine(actuatorAnchor, src, 'appliance');
                });

                highlightedDetectorIds.forEach((detectorId) => {
                    const detector = droppedDetectors.find((item) => item.id === detectorId);
                    if (detector) {
                        pushLine(src, detector, 'detector');
                    }
                });
            } else if (hoveredItem.kind === 'controller') {
                const src = controllerPositions[hoveredItem.id];
                if (!src) return [];
                const hoveredController = controllers.find((item) => item.id === hoveredItem.id);
                if (!hoveredController) return [];

                if (showActuators) {
                    const linkedActuatorIds = getControllerConnectedActuatorIds(hoveredController);

                    Array.from(linkedActuatorIds).forEach((actuatorId) => {
                        const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                        if (!droppedActuator || droppedActuator.dinrail) {
                            return;
                        }

                        const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                        pushLine(src, actuatorAnchor ?? undefined, 'actuator');
                    });
                }

                const builtInActuator = getBuiltInActuatorForController(hoveredController.id);
                const isDirectConnectThermostat = hoveredController.type === 'thermostat'
                    && (hoveredController.thermostatRequiredActuator === null || hoveredController.thermostatRequiredActuator === undefined);

                highlightedApplianceIds.forEach((id) => {
                    const appliance = droppedItems.find((i) => i.id === id);
                    if (appliance) {
                        if (isDirectConnectThermostat) {
                            pushLine(src, appliance, 'controller');
                            return;
                        }

                        const dinrailActuators = getDinrailActuatorsForAppliance(id);

                        if (dinrailActuators.length > 0) {
                            pushLine(src, appliance, 'appliance');
                        }

                        // Built-in actuator on this controller connects straight to the appliance.
                        if (builtInActuator && isApplianceConnectedToActuator(builtInActuator, id)) {
                            pushLine(src, appliance, 'actuator');
                        }

                        highlightedActuatorIds.forEach((actuatorId) => {
                            const actuator = actuators.find((a) => a.id === actuatorId);
                            const droppedActuator = droppedActuators.find((i) => i.id === actuatorId);
                            if (isApplianceConnectedToActuator(actuator, id) && droppedActuator && !droppedActuator.dinrail) {
                                const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                                if (!actuatorAnchor) {
                                    return;
                                }
                                // Draw line from actuator to appliance (actuator -> appliance)
                                pushLine(actuatorAnchor, appliance, 'appliance');
                                // Draw line from controller to actuator (controller -> actuator)
                                pushLine(src, actuatorAnchor, 'actuator');
                            }
                        });
                    }
                });
            } else if (hoveredItem.kind === 'actuator') {
                const src = getConnectionAnchorPoint(droppedActuators.find((i) => i.id === hoveredItem.id));
                if (!src) return [];

                const directlyLinkedControllerIds = getControllerIdsForActuator(hoveredItem.id);
                directlyLinkedControllerIds.forEach((controllerId) => {
                    pushLine(controllerPositions[controllerId], src, 'actuator');
                });

                highlightedApplianceIds.forEach((applianceId) => {
                    const appliance = droppedItems.find((i) => i.id === applianceId);
                    if (!appliance) {
                        return;
                    }

                    pushLine(src, appliance, 'appliance');

                    const controllerIds = controllers
                        .filter((controller) => controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(applianceId)))
                        .map((controller) => controller.id);

                    controllerIds.forEach((controllerId) => {
                        pushLine(controllerPositions[controllerId], src, 'actuator');
                    });
                });
            } else if (hoveredItem.kind === 'detector') {
                const detector = droppedDetectors.find((item) => item.id === hoveredItem.id);
                if (!detector) return [];

                getDetectorConnectedActuatorIds(detectors.find((item) => item.id === hoveredItem.id)).forEach((actuatorId) => {
                    const droppedActuator = droppedActuators.find((item) => item.id === actuatorId);
                    if (!droppedActuator || droppedActuator.dinrail || !showActuators) {
                        return;
                    }

                    const actuatorAnchor = getConnectionAnchorPoint(droppedActuator);
                    if (!actuatorAnchor) return;

                    pushLine(detector, actuatorAnchor, 'detector');
                    getConnectedApplianceIds(actuators.find((item) => item.id === actuatorId)).forEach((applianceId) => {
                        const appliance = droppedItems.find((item) => item.id === applianceId);
                        if (!appliance) return;

                        pushLine(actuatorAnchor, appliance, 'appliance');
                    });
                });
            }

            return lines;
        }
    // eslint-disable-next-line
    }, [tieLinesMode, hoveredItem, droppedItems, droppedActuators, droppedDetectors, controllerPositions, highlightedApplianceIds, highlightedActuatorIds, highlightedDetectorIds, actuators, controllers, detectors, isEasyMode, getControllerIdsForActuator, getControllerIdsForAppliance, getControllerLinkedActuatorIds, getDinrailActuatorsForAppliance, showActuators, isMenuHoveredItem, getBuiltInActuatorForController, getBuiltInActuatorControllerIdsForAppliance]);

    const renderedConnectionLines = useMemo(() => {
        return connectionLines.map(trimConnectionLineToCircleEdge);
    }, [connectionLines, trimConnectionLineToCircleEdge]);

    const saveEasyModeActuatorSelection = (applianceId: number, actuatorType: string) => {
        if (!actuatorType) {
            return;
        }


        const template = actuatorTemplates.find((a) => a.type === actuatorType);
        if (!template) {
            return;
        }

        const newActuatorId = nextDroppedActuatorIdRef.current;
        nextDroppedActuatorIdRef.current += 1;

        const appliancePosition = droppedItems.find((item) => item.id === applianceId)
            ?? appliancesRef.current.find((item) => item.id === applianceId);

        if (!appliancePosition) {
            return;
        }

        const applianceX = typeof appliancePosition.x === 'number' ? appliancePosition.x : 50;
        const applianceY = typeof appliancePosition.y === 'number' ? appliancePosition.y : 50;
        const horizontalOffset = applianceX >= 92 ? -5 : 5;
        const actuatorX = Math.max(0, Math.min(100, applianceX + horizontalOffset));
        const actuatorY = applianceY;

        const newDroppedActuator: DroppedFloorplanItem & { refImg: string } = {
            id: newActuatorId,
            icon: template.icon,
            title: template.name,
            type: 'actuator',
            modelType: template.type,
            applianceType: template.applianceType,
            state: 'warning',
            x: actuatorX,
            y: actuatorY,
            dinrail: template.dinrail ?? false,
            refImg: template.refImg
        };

        if (newDroppedActuator.dinrail) {
            dispatch(setBottomBarOpen(true));
        }

        setDroppedActuators((current) => [...current, newDroppedActuator]);

        // Connect actuator → appliance and appliance → actuator
        dispatch(setActuators(
            [...actuatorsRef.current, {
                ...template,
                id: newActuatorId,
                x: actuatorX,
                y: actuatorY,
                numberOfChannels: template.numberOfChannels ?? 1,
                connectedAppliancesByChannel: [{ channel: 1, applianceIds: [applianceId] }],
                connectedAppliances: [applianceId],
                state: 'warning'
            }]
        ));

        dispatch(setAppliances(
            appliancesRef.current.map((appliance) =>
                appliance.id === applianceId
                    ? { ...appliance, actuator: newActuatorId }
                    : appliance
            )
        ));
    };

    const closeEasyModeDropPopup = () => {
        setEasyModeDropPopup(null);
        setEasyModePopupStep('selectActuator');
        setEasyModeSelectedActuator('');
    };

    const openNewDeviceFromEasyMode = (applianceId: number) => {
        const appliancePosition = droppedItems.find((item) => item.id === applianceId)
            ?? appliancesRef.current.find((item) => item.id === applianceId);

        const applianceX = typeof appliancePosition?.x === 'number' ? appliancePosition.x : 50;
        const applianceY = typeof appliancePosition?.y === 'number' ? appliancePosition.y : 50;
        const easyModeControllerOffsetX = 12;
        const suggestedX = Math.max(0, Math.min(100, applianceX + (applianceX >= 90 ? -easyModeControllerOffsetX : easyModeControllerOffsetX)));
        const suggestedY = Math.max(0, Math.min(100, applianceY));

        dispatch(setSelectedDevicePosition({ x: suggestedX, y: suggestedY }));
        dispatch(setSelectingDevice(true));
        closeEasyModeDropPopup();
    };

    const handleEasyModePopupNext = (applianceId: number, selectedActuatorType?: string) => {
        saveEasyModeActuatorSelection(applianceId, selectedActuatorType ?? easyModeSelectedActuator);
        setEasyModePopupStep('addControllerQuestion');
    };

    const handleEasyModeAddControllerChoice = (shouldOpenNewDevice: boolean, applianceId: number) => {
        if (shouldOpenNewDevice) {
            openNewDeviceFromEasyMode(applianceId);
            return;
        }

        closeEasyModeDropPopup();
    };

    const leftPadding = leftMenuShown ? '30rem' : '8rem';
    const rightPadding = rightMenuShown ? '50rem' : '8rem';

    return (
        <div
            id="tutorial-floorplan"
            className={styles.container}
            style={{
                paddingLeft: leftPadding,
                paddingRight: rightPadding,
                '--icon-size': iconSize,
            } as React.CSSProperties}
        >
            {/* <div className={styles.toggleButtons} style={{ paddingLeft: leftPadding }}>
                <div className={styles.toggleButtons__wrapper}>
                    <div className={styles.toggleButtons__item}>
                        <p>{t('floorplan.show_hide_actuators')}</p>
                        <Checkbox
                            onChange={() => setShowActuators(prev => !prev)}
                            checked={showActuators}
                        ></Checkbox>
                    </div>
                    <div className={styles.toggleButtons__item}>
                        <p>{t('floorplan.show_hide_tielines')}</p>
                        <Checkbox
                            onChange={() => setShowTieLines(prev => !prev)}
                            checked={showTieLines}
                        ></Checkbox>
                    </div>
                    <div className={styles.toggleButtons__item}>
                        <p>{t('floorplan.show_hide_background_colors')}</p>
                        <Checkbox
                            onChange={() => setShowBackgroundColors(prev => !prev)}
                        ></Checkbox>
                    </div>
                </div>
            </div> */}
            <div
                id={FLOORPLAN_EXPORT_TARGET_ID}
                ref={dropZoneRef}
                className={styles.dropZone}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <img
                    className={styles.floorplanImage}
                    draggable="false"
                    src={floorplanImage ?? addPublicURL('default_floorplan.png')}
                    alt="Floorplan"
                />
                {controllers.map((controller) => {
                    const position = controllerPositions[controller.id] || { x: 50, y: 50 };
                    const title = `${controller.name}${controller.colorShortcut}`;
                    const isHovered = hoveredItem?.kind === 'controller' && hoveredItem.id === controller.id;
                    const isLinkedHighlight = highlightedControllerIds.has(controller.id);
                    const controllerIndicator = controller.type === 'thermostat'
                        ? `${controller.state === 'ok' ? 1 : 0}/1`
                        : `${getControllerAssignedSlotsCount(controller)}/${controller.icons}`;
                    const controllerStateClass = getStateBackgroundClassName(controller.state);
                    const builtInActuator = actuators.find((actuator) => actuator.builtIn && actuator.linkedControllerId === controller.id);
                    return (
                        <div
                            key={`controller-${controller.id}`}
                            className={`${styles.droppedItem} ${controllerStateClass} ${isHovered ? getItemHighlightClassName('controller', 'hovered') : ''} ${isLinkedHighlight ? getItemHighlightClassName('controller', 'linked') : ''}`}
                            style={{ left: `${position.x}%`, top: `${position.y}%` }}
                            title={title}
                            onMouseDown={(event) => handleControllerMouseDown(event, controller.id, title, controller.previewImage, controller.color)}
                            onTouchStart={(event) => handleControllerTouchStart(event, controller.id, title, controller.previewImage)}
                            onDoubleClick={() => showDeviceDetail(controller.id, controller.type)}
                            onMouseEnter={() => dispatch(setHoveredItem({ kind: 'controller', id: controller.id, source: 'floorplan' }))}
                            onMouseLeave={() => dispatch(setHoveredItem(null))}
                        >
                            {controller && (
                                <>
                                    {controller.type !== 'thermostat' && (
                                        <div className={styles.droppedItem__count_indicator}>
                                            <span>{controllerIndicator}</span>
                                        </div>
                                    )}
                                    <div className={styles.droppedItem__id_indicator}>
                                        <span>{formatOptionalEntityId('controller', controller.id)}</span>
                                    </div>
                                    {builtInActuator && (
                                        <div
                                            className={styles.droppedItem__actuator_indicator}
                                            onMouseDown={(event) => event.stopPropagation()}
                                            onClick={(event) => { event.stopPropagation(); showActuatorDetail(builtInActuator.id ?? 0); }}
                                        >
                                            <img draggable={false} src={getIconPath('actuator_box', '.svg', 'actuators')} alt="Built-in actuator" />
                                        </div>
                                    )}
                                </>
                            )}
                            <img draggable={false} src={addPublicURL(`/assets/controllers/preview/${controller.previewImage + '-' + controller.color}.png`)} alt={title} />
                        </div>
                    );
                })}
                {droppedItems.map((item) => {
                    const isHovered = hoveredItem?.kind === 'appliance' && hoveredItem.id === item.id;
                    const isLinkedHighlight = highlightedApplianceIds.has(item.id);
                    const state = appliances.find((appliance) => appliance.id === item.id)?.state ?? 'error';
                    const applianceStateClass = getStateBackgroundClassName(state);
                    return (
                        <React.Fragment key={item.id}>
                            <div
                                className={`${styles.droppedItem} ${applianceStateClass} ${isHovered ? getItemHighlightClassName('appliance', 'hovered') : ''} ${isLinkedHighlight ? getItemHighlightClassName('appliance', 'linked') : ''}`}
                                style={{ left: `${item.x}%`, top: `${item.y}%` }}
                                title={item.title}
                                onMouseDown={(event) => handlePlacedItemMouseDown(event, item, getIconPath(item.icon, '.svg', 'controllers/black'))}
                                onTouchStart={(event) => handlePlacedItemTouchStart(event, item)}
                                onMouseEnter={() => dispatch(setHoveredItem({ kind: 'appliance', id: item.id, source: 'floorplan' }))}
                                onMouseLeave={() => dispatch(setHoveredItem(null))}
                                onDoubleClick={() => showApplianceDetail(item.id)}
                            >
                                <img draggable={false} src={getIconPath(item.icon, '.svg', 'controllers/black')} alt={item.title} />
                                <div className={styles.droppedItem__id_indicator}>
                                    <span>{formatOptionalEntityId('appliance', item.id)}</span>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
                {droppedActuators.map((item) => {
                    if (!showActuators || item.dinrail) {
                        return null;
                    }
                    const isActuatorHovered = hoveredItem?.kind === 'actuator' && hoveredItem.id === item.id;
                    const isActuatorLinked = highlightedActuatorIds.has(item.id);
                    const a = actuators.find((actuator) => actuator.id === item.id);
                    const usedChannels = getUsedActuatorChannels(a);
                    const totalChannels = getActuatorNumberOfChannels(a);
                    const actuatorIndicator = `${usedChannels}/${totalChannels}`;
                    const actuatorStateClass = getStateBackgroundClassName(a?.state);
                    return (
                        <div
                            key={`actuator-${item.id}`}
                            className={`${styles.droppedItem} ${actuatorStateClass} ${isActuatorHovered ? getItemHighlightClassName('actuator', 'hovered') : ''} ${isActuatorLinked ? getItemHighlightClassName('actuator', 'linked') : ''}`}
                            style={{ left: `${item.x}%`, top: `${item.y}%` }}
                            title={item.title}
                            onMouseDown={(event) => handlePlacedItemMouseDown(event, item, getIconPath(item.icon, '.svg', 'actuators'))}
                            onTouchStart={(event) => handlePlacedItemTouchStart(event, item)}
                            onDoubleClick={() => showActuatorDetail(item.id)}
                            onMouseEnter={() => dispatch(setHoveredItem({ kind: 'actuator', id: item.id, source: 'floorplan' }))}
                            onMouseLeave={() => dispatch(setHoveredItem(null))}
                        >
                            {a && (
                                <>
                                    <div className={styles.droppedItem__count_indicator}>
                                        <span>{actuatorIndicator}</span>
                                    </div>
                                    <div className={styles.droppedItem__id_indicator}>
                                        <span>{formatOptionalEntityId('actuator', item.id)}</span>
                                    </div>
                                </>
                            )}
                            <img draggable={false} src={getIconPath(item.icon, '.svg', 'actuators')} alt={item.modelType} />
                        </div>
                    );
                })}
                {droppedDetectors.map((item) => {
                    const isDetectorHovered = hoveredItem?.kind === 'detector' && hoveredItem.id === item.id;
                    const isDetectorLinked = highlightedDetectorIds.has(item.id);
                    const d = detectors.find((detector) => detector.id === item.id);
                    const detectorStateClass = getStateBackgroundClassName(d?.state);
                    return (
                        <div
                            key={`detector-${item.id}`}
                            className={`${styles.droppedItem} ${detectorStateClass} ${isDetectorHovered ? getItemHighlightClassName('detector', 'hovered') : ''} ${isDetectorLinked ? getItemHighlightClassName('detector', 'linked') : ''}`}
                            style={{ left: `${item.x}%`, top: `${item.y}%` }}
                            title={item.title}
                            onMouseDown={(event) => handlePlacedItemMouseDown(event, item, addPublicURL(`assets/detectors/${item.icon}`))}
                            onTouchStart={(event) => handlePlacedItemTouchStart(event, item)}
                            onDoubleClick={() => showDetectorDetail(item.id)}
                            onMouseEnter={() => dispatch(setHoveredItem({ kind: 'detector', id: item.id, source: 'floorplan' }))}
                            onMouseLeave={() => dispatch(setHoveredItem(null))}
                        >
                            {d && (
                                <>
                                    <div className={styles.droppedItem__id_indicator}>
                                        <span>{formatOptionalEntityId('detector', item.id)}</span>
                                    </div>
                                </>
                            )}
                            <img draggable={false} src={addPublicURL(`assets/detectors/${item.icon}`)} alt={item.title} />
                        </div>
                    );
                })}
                {currentDinrailMarkers.map((marker) => (
                    <div
                        key={`dinrail-marker-${marker.key}`}
                        className={styles.dinrailMarker}
                        style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                        title={marker.title}
                    >
                        <img draggable={false} src={getIconPath(marker.icon, '.svg', 'actuators')} alt={marker.title} />
                    </div>
                ))}
                {renderedConnectionLines.length > 0 && (
                    <>
                        <svg
                            className={styles.connectionLines}
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                        >
                            {renderedConnectionLines.map((line, i) => (
                                <line
                                    key={i}
                                    x1={line.x1}
                                    y1={line.y1}
                                    x2={line.x2}
                                    y2={line.y2}
                                    className={getConnectionLineClassName(line.kind)}
                                    strokeWidth="1"
                                    vectorEffect="non-scaling-stroke"
                                />
                            ))}
                        </svg>
                        {renderedConnectionLines.map((line, i) => {
                            const midX = (line.x1 + line.x2) / 2;
                            const midY = (line.y1 + line.y2) / 2;
                            const angle = (Math.atan2(
                                (line.y2 - line.y1) * dropZoneSize.height,
                                (line.x2 - line.x1) * dropZoneSize.width
                            ) * 180) / Math.PI;

                            return (
                                <div
                                    key={`arrow-${i}`}
                                    className={`${styles.connectionArrowMarker} ${getConnectionArrowClassName(line.kind)}`}
                                    style={{
                                        left: `${midX}%`,
                                        top: `${midY}%`,
                                        transform: `translate(-50%, -50%) rotate(${angle}deg)`
                                    }}
                                />
                            );
                        })}
                    </>
                )}
                {easyModeDropPopup && easyModeDropPopup.items !== undefined && (
                    <div
                        className={styles.easyModeDropPopup}
                        style={{ left: `${easyModeDropPopup.x}%`, top: `${easyModeDropPopup.y}%` }}
                    >
                        <div className={styles.easyModeDropPopup__content}>
                            {easyModePopupStep === 'selectActuator' ? (
                                <>
                                    <h4>{Object.keys(easyModeDropPopup.items).length > 1 ? t('easyMode.dropPopup.title') : t('easyMode.dropPopup.singleItemTitle')}</h4>
                                    <div className={styles.easyModeDropPopup__items}>
                                        {easyModeDropPopup?.items && Object.keys(easyModeDropPopup.items).length > 1 ? (
                                            Object.entries(easyModeDropPopup.items).map(([key, value]) => (
                                                <div className={styles.easyModeDropPopup__item} key={key}>
                                                    <Checkbox
                                                        onChange={() => setEasyModeSelectedActuator(easyModeSelectedActuator === value ? '' : value)}
                                                        checked={easyModeSelectedActuator === value}
                                                        variant='outline'
                                                    >
                                                    </Checkbox>
                                                    <p>{t(`easyMode.dropPopup.items.${key}`)}</p>
                                                </div>

                                            ))
                                        ) : null}
                                        {easyModeDropPopup?.items && Object.keys(easyModeDropPopup.items).length === 1 ? (
                                            Object.entries(easyModeDropPopup.items).map(([key, value]) => (
                                                <div className={styles.easyModeDropPopup__singleItem} key={key}>
                                                    {/* <Checkbox
                                                        onChange={() => setEasyModeSelectedActuator(easyModeSelectedActuator === value ? '' : value)}
                                                        checked={easyModeSelectedActuator === value}
                                                    >
                                                    </Checkbox>
                                                    <p>{t(`easyMode.dropPopup.items.${key}`)}</p> */}
                                                    <Button size="md" variant="light"
                                                        onClick={() => handleEasyModePopupNext(easyModeDropPopup.applianceId)}
                                                        >{t(`easyMode.dropPopup.addActuatorQuestion.no`)}
                                                    </Button>
                                                    <Button size="md" variant="fill"
                                                        onClick={() => {
                                                            setEasyModeSelectedActuator(value);
                                                            handleEasyModePopupNext(easyModeDropPopup.applianceId, value);
                                                        }}
                                                        >{t(`easyMode.dropPopup.addActuatorQuestion.yes`)}
                                                    </Button>
                                                </div>
                                            ))
                                        ) : null}
                                    </div>
                                    {Object.keys(easyModeDropPopup.items).length > 1 && (
                                        <div className={styles.easyModeDropPopup__actions}>
                                            <Button
                                                size="sm"
                                                onClick={() => closeEasyModeDropPopup()}
                                                variant='light'
                                            >{t('close')}</Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleEasyModePopupNext(easyModeDropPopup.applianceId)}
                                                variant='fill'
                                            >{t('next')}</Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <h4>{t('easyMode.dropPopup.addControllerQuestion.title')}</h4>
                                    <div className={styles.easyModeDropPopup__items}>
                                        <p>{t('easyMode.dropPopup.addControllerQuestion.description')}</p>
                                    </div>
                                    <div className={styles.easyModeDropPopup__actions}>
                                        <Button
                                            size="sm"
                                            onClick={() => handleEasyModeAddControllerChoice(false, easyModeDropPopup.applianceId)}
                                            variant='light'
                                        >{t('easyMode.dropPopup.addControllerQuestion.no')}</Button>
                                        <Button
                                            size="sm"
                                            onClick={() => handleEasyModeAddControllerChoice(true, easyModeDropPopup.applianceId)}
                                            variant='fill'
                                        >{t('easyMode.dropPopup.addControllerQuestion.yes')}</Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {activeDraggedItem && dragCursorPosition && (
                <div
                    className={styles.dragFollower}
                    style={{ left: `${dragCursorPosition.x}px`, top: `${dragCursorPosition.y}px` }}
                >
                    <img draggable={false} src={activeDraggedItem.imgSrc} alt={activeDraggedItem.title} />
                </div>
            )}
        </div>
    );
}

export default FloorplanComponent;